import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { trpcClient } from "@/clients/trpcClient";
import { isModerationConflict, MODERATION_CONFLICT_MESSAGE } from "@/lib/utils/moderationConflict";
import { trpc } from "@/router";

/**
 * Drives the admin review dashboard at /admin/granska.
 *
 * Owns:
 * - which tab is active (places / features / all)
 * - list vs map view toggle
 * - which item is selected (id + type)
 * - moderate mutations (approve, reject) for places and features
 * - reposition mutations for places and features
 * - keyboard handlers (j/k/e/r/m)
 *
 * Views are pure presentation — all logic lives here.
 */

/** Shared shape used by list rows and map pins. */

export function useReviewDashboard() {
  const $ = _c(81);
  const queryClient = useQueryClient();
  const [tab, setTab] = useState("places");
  const [view, setView] = useState("list");
  const [selection, setSelection] = useState(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = trpc.places.getPending.queryOptions();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const placesQuery = useQuery(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = trpc.features.getPendingFeatures.queryOptions();
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const featuresQuery = useQuery(t1);
  let t2;
  if ($[2] !== placesQuery.data) {
    t2 = placesQuery.data ?? [];
    $[2] = placesQuery.data;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = t2.map(_temp);
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const placeItems = t3;
  let t4;
  if ($[6] !== featuresQuery.data) {
    t4 = featuresQuery.data ?? [];
    $[6] = featuresQuery.data;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = t4.map(_temp2);
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const featureItems = t5;
  let t6;
  if ($[10] !== featureItems) {
    const counts = new Map();
    for (const f_0 of featureItems) {
      const name = f_0.tags[0] ?? "Ok\xE4nd";
      counts.set(name, (counts.get(name) ?? 0) + 1);
    }
    t6 = Array.from(counts.entries()).map(_temp3).sort(_temp4);
    $[10] = featureItems;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const featureTypeCounts = t6;
  let t7;
  if ($[12] !== featureItems || $[13] !== placeItems || $[14] !== tab || $[15] !== typeFilter) {
    bb0: {
      if (tab === "places") {
        t7 = placeItems;
        break bb0;
      }
      const filteredFeatures = typeFilter ? featureItems.filter(f_1 => f_1.tags[0] === typeFilter) : featureItems;
      if (tab === "features") {
        t7 = filteredFeatures;
        break bb0;
      }
      t7 = [...placeItems, ...filteredFeatures].sort(_temp5);
    }
    $[12] = featureItems;
    $[13] = placeItems;
    $[14] = tab;
    $[15] = typeFilter;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const items = t7;
  let t8;
  let t9;
  if ($[17] !== featureTypeCounts || $[18] !== typeFilter) {
    t8 = () => {
      if (typeFilter && !featureTypeCounts.some(t => t.name === typeFilter)) {
        setTypeFilter(null);
      }
    };
    t9 = [typeFilter, featureTypeCounts];
    $[17] = featureTypeCounts;
    $[18] = typeFilter;
    $[19] = t8;
    $[20] = t9;
  } else {
    t8 = $[19];
    t9 = $[20];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[21] !== items || $[22] !== selection) {
    t10 = () => {
      if (items.length === 0) {
        setSelection(null);
        return;
      }
      const stillPresent = selection && items.some(i => i.type === selection.type && i.id === selection.id);
      if (!stillPresent) {
        setSelection({
          type: items[0].type,
          id: items[0].id
        });
      }
    };
    t11 = [items, selection];
    $[21] = items;
    $[22] = selection;
    $[23] = t10;
    $[24] = t11;
  } else {
    t10 = $[23];
    t11 = $[24];
  }
  useEffect(t10, t11);
  const t12 = placeItems.length + featureItems.length;
  let t13;
  if ($[25] !== featureItems.length || $[26] !== placeItems.length || $[27] !== t12) {
    t13 = {
      places: placeItems.length,
      features: featureItems.length,
      all: t12
    };
    $[25] = featureItems.length;
    $[26] = placeItems.length;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  const counts_0 = t13;
  let t14;
  if ($[29] !== queryClient) {
    t14 = () => {
      queryClient.invalidateQueries({
        queryKey: [["places"]]
      });
      queryClient.invalidateQueries({
        queryKey: [["features"]]
      });
      queryClient.invalidateQueries({
        queryKey: [["moderation"]]
      });
    };
    $[29] = queryClient;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const invalidate = t14;
  let t15;
  if ($[31] !== invalidate) {
    t15 = {
      mutationFn: _temp6,
      onSuccess: (_res, target_0) => {
        toast.success(target_0.type === "place" ? "Plats godk\xE4nd" : "Funktion godk\xE4nd");
        invalidate();
      },
      onError: err => {
        if (isModerationConflict(err)) {
          toast.error(MODERATION_CONFLICT_MESSAGE);
          invalidate();
          return;
        }
        const msg = err instanceof Error && err.message.includes("PARENT_NOT_APPROVED") ? "Godk\xE4nn platsen f\xF6rst \u2014 funktioner kan inte godk\xE4nnas innan dess." : "Kunde inte godk\xE4nna";
        toast.error(msg);
      }
    };
    $[31] = invalidate;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  const approveMutation = useMutation(t15);
  let t16;
  if ($[33] !== invalidate) {
    t16 = {
      mutationFn: _temp7,
      onSuccess: (_res_0, t17) => {
        const {
          target: target_2
        } = t17;
        toast.success(target_2.type === "place" ? "Plats avvisad" : "Funktion avvisad");
        invalidate();
      },
      onError: err_0 => {
        if (isModerationConflict(err_0)) {
          toast.error(MODERATION_CONFLICT_MESSAGE);
          invalidate();
          return;
        }
        toast.error("Kunde inte avvisa");
      }
    };
    $[33] = invalidate;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  const rejectMutation = useMutation(t16);
  let t17;
  if ($[35] !== invalidate) {
    t17 = {
      mutationFn: _temp8,
      onSuccess: (_res_1, t18) => {
        const {
          location: location_0
        } = t18;
        toast.success(location_0 ? "Position uppdaterad" : "Position rensad");
        invalidate();
      },
      onError: _temp9
    };
    $[35] = invalidate;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  const repositionMutation = useMutation(t17);
  let t18;
  if ($[37] !== approveMutation || $[38] !== selection) {
    t18 = () => {
      if (selection) {
        approveMutation.mutate(selection);
      }
    };
    $[37] = approveMutation;
    $[38] = selection;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  const approve = t18;
  let t19;
  if ($[40] !== rejectMutation || $[41] !== selection) {
    t19 = reason_0 => {
      if (selection) {
        rejectMutation.mutate({
          target: selection,
          reason: reason_0
        });
      }
    };
    $[40] = rejectMutation;
    $[41] = selection;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  const reject = t19;
  let t20;
  if ($[43] !== selection) {
    t20 = () => {
      if (selection) {
        setRejectDialogOpen(true);
      }
    };
    $[43] = selection;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  const openRejectDialog = t20;
  let t21;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = () => {
      setRejectDialogOpen(false);
    };
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  const closeRejectDialog = t21;
  let t22;
  if ($[46] !== repositionMutation || $[47] !== selection) {
    t22 = location_1 => {
      if (selection) {
        repositionMutation.mutate({
          target: selection,
          location: location_1
        });
      }
    };
    $[46] = repositionMutation;
    $[47] = selection;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  const reposition = t22;
  let t23;
  if ($[49] !== items || $[50] !== selection) {
    t23 = selection ? items.findIndex(i_0 => i_0.type === selection.type && i_0.id === selection.id) : -1;
    $[49] = items;
    $[50] = selection;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  const selectedIndex = t23;
  let t24;
  if ($[52] !== items || $[53] !== selectedIndex) {
    t24 = () => {
      if (items.length === 0) {
        return;
      }
      const idx = selectedIndex < 0 ? 0 : Math.min(selectedIndex + 1, items.length - 1);
      const target_4 = items[idx];
      setSelection({
        type: target_4.type,
        id: target_4.id
      });
    };
    $[52] = items;
    $[53] = selectedIndex;
    $[54] = t24;
  } else {
    t24 = $[54];
  }
  const next = t24;
  let t25;
  if ($[55] !== items || $[56] !== selectedIndex) {
    t25 = () => {
      if (items.length === 0) {
        return;
      }
      const idx_0 = selectedIndex <= 0 ? 0 : selectedIndex - 1;
      const target_5 = items[idx_0];
      setSelection({
        type: target_5.type,
        id: target_5.id
      });
    };
    $[55] = items;
    $[56] = selectedIndex;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  const prev = t25;
  let t26;
  let t27;
  if ($[58] !== approve || $[59] !== next || $[60] !== openRejectDialog || $[61] !== prev) {
    t26 = () => {
      const handler = e => {
        const target_6 = e.target;
        if (target_6 && (target_6.tagName === "INPUT" || target_6.tagName === "TEXTAREA" || target_6.isContentEditable)) {
          return;
        }
        if (e.metaKey || e.ctrlKey || e.altKey) {
          return;
        }
        bb253: switch (e.key) {
          case "j":
            {
              e.preventDefault();
              next();
              break bb253;
            }
          case "k":
            {
              e.preventDefault();
              prev();
              break bb253;
            }
          case "e":
            {
              e.preventDefault();
              approve();
              break bb253;
            }
          case "r":
            {
              e.preventDefault();
              openRejectDialog();
              break bb253;
            }
          case "m":
            {
              e.preventDefault();
              setView(_temp0);
            }
        }
      };
      window.addEventListener("keydown", handler);
      return () => window.removeEventListener("keydown", handler);
    };
    t27 = [next, prev, approve, openRejectDialog];
    $[58] = approve;
    $[59] = next;
    $[60] = openRejectDialog;
    $[61] = prev;
    $[62] = t26;
    $[63] = t27;
  } else {
    t26 = $[62];
    t27 = $[63];
  }
  useEffect(t26, t27);
  const t28 = placesQuery.isLoading || featuresQuery.isLoading;
  let t29;
  if ($[64] !== approve || $[65] !== approveMutation.isPending || $[66] !== counts_0 || $[67] !== featureTypeCounts || $[68] !== items || $[69] !== openRejectDialog || $[70] !== reject || $[71] !== rejectDialogOpen || $[72] !== rejectMutation.isPending || $[73] !== reposition || $[74] !== repositionMutation.isPending || $[75] !== selection || $[76] !== t28 || $[77] !== tab || $[78] !== typeFilter || $[79] !== view) {
    t29 = {
      tab,
      setTab,
      view,
      setView,
      selection,
      select: setSelection,
      items,
      counts: counts_0,
      featureTypeCounts,
      typeFilter,
      setTypeFilter,
      isLoading: t28,
      approve,
      reject,
      reposition,
      rejectDialogOpen,
      openRejectDialog,
      closeRejectDialog,
      isApproving: approveMutation.isPending,
      isRejecting: rejectMutation.isPending,
      isRepositioning: repositionMutation.isPending
    };
    $[64] = approve;
    $[65] = approveMutation.isPending;
    $[66] = counts_0;
    $[67] = featureTypeCounts;
    $[68] = items;
    $[69] = openRejectDialog;
    $[70] = reject;
    $[71] = rejectDialogOpen;
    $[72] = rejectMutation.isPending;
    $[73] = reposition;
    $[74] = repositionMutation.isPending;
    $[75] = selection;
    $[76] = t28;
    $[77] = tab;
    $[78] = typeFilter;
    $[79] = view;
    $[80] = t29;
  } else {
    t29 = $[80];
  }
  return t29;
}
function _temp0(v) {
  return v === "list" ? "map" : "list";
}
function _temp9() {
  return toast.error("Kunde inte uppdatera position");
}
async function _temp8(t0) {
  const {
    target: target_3,
    location
  } = t0;
  if (target_3.type === "place") {
    if (!location) {
      throw new Error("Plats m\xE5ste ha en position");
    }
    return trpcClient.places.reposition.mutate({
      id: target_3.id,
      location
    });
  }
  return trpcClient.features.reposition.mutate({
    id: target_3.id,
    location
  });
}
async function _temp7(t0) {
  const {
    target: target_1,
    reason
  } = t0;
  if (target_1.type === "place") {
    return trpcClient.places.moderate.mutate({
      id: target_1.id,
      action: "reject",
      reason,
      expectedStatus: "pending"
    });
  }
  return trpcClient.features.moderate.mutate({
    id: target_1.id,
    action: "reject",
    reason,
    expectedStatus: "pending"
  });
}
async function _temp6(target) {
  if (target.type === "place") {
    return trpcClient.places.moderate.mutate({
      id: target.id,
      action: "approve",
      expectedStatus: "pending"
    });
  }
  return trpcClient.features.moderate.mutate({
    id: target.id,
    action: "approve",
    expectedStatus: "pending"
  });
}
function _temp5(a_0, b_0) {
  return a_0.createdAt.getTime() - b_0.createdAt.getTime();
}
function _temp4(a, b) {
  return b.count - a.count;
}
function _temp3(t0) {
  const [name_0, count] = t0;
  return {
    name: name_0,
    count
  };
}
function _temp2(f) {
  return {
    type: "feature",
    id: f.id,
    title: f.place?.name ?? "Ok\xE4nd plats",
    subtitle: f.featureTypeName ?? "Funktion",
    location: f.location ? {
      latitude: f.location.y,
      longitude: f.location.x
    } : null,
    createdAt: new Date(f.createdAt),
    creatorName: f.creator?.name ?? null,
    tags: f.featureTypeName ? [f.featureTypeName] : [],
    needsRecheck: Boolean(f.needsRecheck)
  };
}
function _temp(p) {
  return {
    type: "place",
    id: p.id,
    title: p.name,
    subtitle: p.featureCount ? `${p.featureCount} bifogad${p.featureCount === 1 ? "" : "e"} funktion${p.featureCount === 1 ? "" : "er"}` : "Inga funktioner bifogade",
    location: p.location ? {
      latitude: p.location.y,
      longitude: p.location.x
    } : null,
    createdAt: new Date(p.createdAt),
    creatorName: p.creator?.name ?? null,
    tags: p.categoryName ? [p.categoryName] : []
  };
}